// OptionsDynamicWaveForm.jsx
import { useState, useEffect } from "react"
import { UniversalDropDown } from "@fileops/swmm-react"
import { Typography, Grid } from '@mui/material'
import "../../css/DemoCode.css"

/**
 * Options Dynamic Wave Form is a group of data inputs usually shown when clicking on Options->Dynamic Wave. 
 * 
 * @param {JSON} model A SWMM model in JSON format. In most cases this
 * is a 'submodel' containing only a copy of the affected sections. Using a copy allows
 * for continuous react-style editing without forcing the model to run on every keystroke.
 * @param {function} setModel Update function for updating 'model'.
 * @param {JSON} fullModel The 'full' SWMM model that contains supporting data for this component.
 * @returns UI for interactive swmm modeling.
 */
export default function OptionsDynamicWaveForm({model, setModel, fullModel}) {
  const [inertialTerm, setInertialTerm] = useState(fullModel.OPTIONS.INERTIAL_DAMPING?fullModel.OPTIONS.INERTIAL_DAMPING:'PARTIAL')
  const [inertialTermSelected, setInertialTermSelected] = useState('none')
  const [inertialIDs, setInertialIDs] = useState(['Keep', 'Dampen', 'Ignore'])
  const [normalFlowCriterion, setNormalFlowCriterion] = useState(fullModel.OPTIONS.NORMAL_FLOW_LIMITED?fullModel.OPTIONS.NORMAL_FLOW_LIMITED:'SLOPE')
  const [normalFlowCriterionSelected, setNormalFlowCriterionSelected] = useState('none')
  const [normalFlowIDs, setNormalFlowIDs] = useState(['Slope', 'Froude No.', 'Slope & Froude', 'None'])
  const [forcemainEquation, setForcemainEquation] = useState(fullModel.OPTIONS.FORCE_MAIN_EQUATION?fullModel.OPTIONS.FORCE_MAIN_EQUATION:'H-W')
  const [forcemainEquationSelected, setForcemainEquationSelected] = useState('none')
  const [forcemainEquationIDs, setForcemainEquationIDs] = useState(['Hazen-Williams', 'Darcy-Weisbach'])
  const [surchargeMethod, setSurchargeMethod] = useState(fullModel.OPTIONS.SURCHARGE_METHOD?fullModel.OPTIONS.SURCHARGE_METHOD:'EXTRAN')
  const [surchargeMethodSelected, setSurchargeMethodSelected] = useState('none')
  const [surchargeMethodIDs, setSurchargeMethodIDs] = useState(['Extran', 'Slot'])

  const [variableTimeSteps, setVariableTimeSteps] = useState(fullModel.OPTIONS.VARIABLE_STEP?fullModel.OPTIONS.VARIABLE_STEP:'0.75')
  const [minimumVariableTimeStep, setMinimumVariableTimeStep] = useState(fullModel.OPTIONS.MINIMUM_STEP?fullModel.OPTIONS.MINIMUM_STEP:'0.5')
  const [conduitLengtheningStep, setConduitLengtheningStep] = useState(fullModel.OPTIONS.LENGTHENING_STEP?fullModel.OPTIONS.LENGTHENING_STEP:'0')
  const [minNodalSurfArea, setMinNodalSurfArea] = useState(fullModel.OPTIONS.MIN_SURFAREA?fullModel.OPTIONS.MIN_SURFAREA:'12.557')
  const [headConvergenceTolerance, setHeadConvergenceTolerance] = useState(fullModel.OPTIONS.HEAD_TOLERANCE?fullModel.OPTIONS.HEAD_TOLERANCE:'0.005')
  const [maxTrialsPerTimeStep, setMaxTrialsPerTimeStep] = useState(fullModel.OPTIONS.MAX_TRIALS?fullModel.OPTIONS.MAX_TRIALS:'5')
  const [threads, setThreads] = useState(fullModel.OPTIONS.THREADS?fullModel.OPTIONS.THREADS:'5')

  useEffect(()=> {
    switch(inertialTerm){
      case 'NONE':
        setInertialTermSelected('Keep')
        break;
      case 'PARTIAL':
        setInertialTermSelected('Dampen')
        break;
      case 'FULL':
        setInertialTermSelected('Ignore')
        break;
    }
    switch(normalFlowCriterion){
      case 'SLOPE':
        setNormalFlowCriterionSelected('Slope')
        break;
      case 'FROUDE':
        setNormalFlowCriterionSelected('Froude No.')
        break;
      case 'BOTH':
        setNormalFlowCriterionSelected('Slope & Froude')
        break;
      case 'NONE':
        setNormalFlowCriterionSelected('None')
        break;
    }
    switch(forcemainEquation){
      case 'H-W':
        setForcemainEquationSelected('Hazen-Williams')
        break;
      case 'D-W':
        setForcemainEquationSelected('Darcy-Weisbach')
        break;
    }
    switch(surchargeMethod){
      case 'EXTRAN':
        setSurchargeMethodSelected('Extran')
        break;
      case 'SLOT':
        setSurchargeMethodSelected('Slot')
        break;
    }
  }, [])

  const updateInertialTerm = (val) => {
    setInertialTerm(val)

    if(val === 'Keep') val = 'NONE'
    else if(val === 'Dampen') val = 'PARTIAL'
    else if(val === 'Ignore') val = 'FULL'
    
    if(model){
      setModel(prev => ({
        ...prev,
        OPTIONS:{
          ...prev.OPTIONS,
          INERTIAL_DAMPING: val
        }
      }))
    }
  }

  const updateNormalFlowCriterion = (val) => {
    setNormalFlowCriterion(val)

    if(val === 'Slope') val = 'SLOPE'
    else if(val === 'Froude No.') val = 'FROUDE'
    else if(val === 'Slope & Froude') val = 'BOTH'
    else if(val === 'None') val = 'NONE'
    
    if(model){
      setModel(prev => ({
        ...prev,
        OPTIONS:{
          ...prev.OPTIONS,
          NORMAL_FLOW_LIMITED: val
        }
      }))
    }
  }

  const updateForceMainEquation = (val) => {
    setForcemainEquation(val)

    if(val === 'Hazen-Williams') val = 'H-W'
    else if(val === 'Darcy-Weisbach') val = 'D-W'
    
    if(model){
      setModel(prev => ({
        ...prev,
        OPTIONS:{
          ...prev.OPTIONS,
          FORCE_MAIN_EQUATION: val
        }
      }))
    }
  }

  const updateSurchargeMethod = (val) => {
    setSurchargeMethod(val)

    if(val === 'Extran') val = 'EXTRAN'
    else if(val === 'Slot') val = 'SLOT'
    
    if(model){
      setModel(prev => ({
        ...prev,
        OPTIONS:{
          ...prev.OPTIONS,
          SURCHARGE_METHOD: val
        }
      }))
    }
  }

  const updateVariableTimeSteps = (val) => {
    setVariableTimeSteps(val)

    if(model){
      setModel(prev => ({
        ...prev,
        OPTIONS:{
          ...prev.OPTIONS,
          VARIABLE_STEP: val
        }
      }))
    }
  }

  const updateMinimumVariableTimeStep = (val) => {
    setMinimumVariableTimeStep(val)
    
    if(model){
      setModel(prev => ({
        ...prev,
        OPTIONS:{
          ...prev.OPTIONS,
          MINIMUM_STEP: val
        }
      }))
    }
  }

  const updateConduitLengtheningStep = (val) => {
    setConduitLengtheningStep(val)
    
    if(model){
      setModel(prev => ({
        ...prev,
        OPTIONS:{
          ...prev.OPTIONS,
          LENGTHENING_STEP: val
        }
      }))
    }
  }
  
  const updateMinimumNodalSurfaceArea = (val) => {
    setMinNodalSurfArea(val)
    
    if(model){
      setModel(prev => ({
        ...prev,
        OPTIONS:{
          ...prev.OPTIONS,
          MIN_SURFAREA: val
        }
      }))
    }
  }
  
  const updateHeadConvergenceTolerance = (val) => {
    setHeadConvergenceTolerance(val)
    
    if(model){
      setModel(prev => ({
        ...prev,
        OPTIONS:{
          ...prev.OPTIONS,
          HEAD_TOLERANCE: val
        }
      }))
    }
  }
  
  const updateTrialsPerTimeStep = (val) => {
    setMaxTrialsPerTimeStep(val)
    
    if(model){
      setModel(prev => ({
        ...prev,
        OPTIONS:{
          ...prev.OPTIONS,
          MAX_TRIALS: val
        }
      }))
    }
  }
  
  const updateThreads = (val) => {
    setThreads(val)
    
    if(model){
      setModel(prev => ({
        ...prev,
        OPTIONS:{
          ...prev.OPTIONS,
          THREADS: val
        }
      }))
    }
  }

  return(
    <>
    {
      <>
      <Grid container>
        <Grid item xs={6}>
        {(
          <>
          <label className='editor-small' >Inertial Terms
            <UniversalDropDown style={{padding: '0'}}
              IDs={inertialIDs} 
              selectText={"Select a Term"} 
              value={inertialTermSelected}
              onChange={(val)=>{updateInertialTerm(val)}} >
            </UniversalDropDown>
          </label>
          <label className='editor-small' >Normal Flow Criterion
            <UniversalDropDown style={{padding: '0'}}
              IDs={normalFlowIDs} 
              selectText={"Select a Criterion"} 
              value={normalFlowCriterionSelected}
              onChange={(val)=>{updateNormalFlowCriterion(val)}} >
            </UniversalDropDown>
          </label>
          <label className='editor-small' >Force Main Equation
            <UniversalDropDown style={{padding: '0'}}
              IDs={forcemainEquationIDs} 
              selectText={"Select an Equation"} 
              value={forcemainEquationSelected}
              onChange={(val)=>{updateForceMainEquation(val)}} >
            </UniversalDropDown>
          </label>
          <label className='editor-small' >Surcharge Method
            <UniversalDropDown style={{padding: '0'}}
              IDs={surchargeMethodIDs} 
              selectText={"Select a Method"} 
              value={surchargeMethodSelected}
              onChange={(val)=>{updateSurchargeMethod(val)}} >
            </UniversalDropDown>
          </label>
          <label className='editor-small' >Variable Time Step Adjustment (fraction)
            <input  type = "number"
                    style={{width: '95%', display: 'flex'}}
                    onChange={(e)=>{updateVariableTimeSteps(e.target.value)}}
                    value={variableTimeSteps}
                    id={'OptionsVariableTimeSteps'}
            />
          </label>
          <label className='editor-small' >Minimum Variable Time Step (sec)
            <input  type = "number"
                    style={{width: '95%', display: 'flex'}}
                    onChange={(e)=>{updateMinimumVariableTimeStep(e.target.value)}}
                    value={minimumVariableTimeStep}
                    id={'OptionsMinimumVariableTimeStep'}
            />
          </label>
          </>
        )}
        </Grid>
        <Grid item xs={6}>
          {
            <>
            <label className='editor-small' >Time Step for Conduit Lengthening (sec)
              <input  type = "number"
                      style={{width: '95%', display: 'flex'}}
                      onChange={(e)=>{updateConduitLengtheningStep(e.target.value)}}
                      value={conduitLengtheningStep}
                      min={0}
                      id={'OptionsConduitLengtheningStep'}
              />
            </label>
            <label className='editor-small' >Minimum Nodal Surface Area
              <input  type = "number"
                      style={{width: '95%', display: 'flex'}}
                      onChange={(e)=>{updateMinimumNodalSurfaceArea(e.target.value)}}
                      value={minNodalSurfArea}
                      min={0}
                      id={'OptionsMinNodalSurfArea'}
              />
            </label>
            <label className='editor-small' >Head Convergence Tolerance
              <input  type = "number"
                      style={{width: '95%', display: 'flex'}}
                      onChange={(e)=>{updateHeadConvergenceTolerance(e.target.value)}}
                      value={headConvergenceTolerance}
                      min={0}
                      id={'OptionsHeadConvergenceTolerance'}
              />
            </label>
            <label className='editor-small' >Maximum Trials per Time Step
              <input  type = "number"
                      style={{width: '95%', display: 'flex'}}
                      onChange={(e)=>{updateTrialsPerTimeStep(e.target.value)}}
                      value={maxTrialsPerTimeStep}
                      min={3}
                      id={'OptionsMaxTrialsPerTimeStep'}
              />
            </label>
            <label className='editor-small' >Number of Parallel Threads to Use
              <input  type = "number"
                      style={{width: '95%', display: 'flex'}}
                      onChange={(e)=>{updateThreads(e.target.value)}}
                      value={threads}
                      min={1}
                      id={'OptionsThreads'}
              />
            </label>
            </>
          }
        </Grid>
      </Grid>
      </>
    }
    </>
  )
} 